// Landing page entry point
// Handles PDF upload (URL + file), auth trigger, and redirect to web-app

import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged 
} from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './landing.css';

// Firebase config - same as web-app
const firebaseConfig = {
  apiKey: "AIzaSyCO_SNK4hGIt2SUVm6yJ2UMqRTjiVxXwLo",
  authDomain: "pelaikan-app.firebaseapp.com",
  projectId: "pelaikan-app",
  storageBucket: "pelaikan-app.firebasestorage.app",
  messagingSenderId: "772587166095",
  appId: "1:772587166095:web:826c89e1d2e5355bdbd3fa",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const storage = getStorage(app);
const googleProvider = new GoogleAuthProvider();

// DOM Elements
const byId = (id) => document.getElementById(id);

// State
let selectedFile = null;
let currentUser = null;

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const pdfUrlInput = byId('pdf-url');
  const pdfFileInput = byId('pdf-file');
  const fileDropZone = byId('file-drop-zone');
  const fileSelected = byId('file-selected');
  const selectedFileName = byId('selected-file-name');
  const fileRemove = byId('file-remove');
  const btnAnalyze = byId('btn-analyze');
  const uploadStatus = byId('upload-status');
  
  // Auth elements
  const authOverlay = byId('auth-overlay');
  const authClose = byId('auth-close');
  const authEmail = byId('auth-email');
  const authPassword = byId('auth-password');
  const btnSignin = byId('btn-signin');
  const btnCreateAccount = byId('btn-create-account');
  const btnGoogleSignin = byId('btn-google-signin');
  const authError = byId('auth-error');
  const authSuccess = byId('auth-success');
  const authLoading = byId('auth-loading');
  
  // Nav auth elements
  const navSignin = byId('nav-signin');
  const navUserMenu = byId('nav-user-menu');
  const navUserEmail = byId('nav-user-email');

  // Listen for auth state changes
  onAuthStateChanged(auth, (user) => {
    currentUser = user;
    if (user) {
      console.log('[Landing] User signed in:', user.email);
      // Update nav to show user menu
      if (navSignin) navSignin.style.display = 'none';
      if (navUserMenu) navUserMenu.style.display = 'flex';
      if (navUserEmail) navUserEmail.textContent = user.email;
      // If we have a pending upload, process it
      if (pendingUpload) {
        processUpload();
      }
    } else {
      // Update nav to show sign in button
      if (navSignin) navSignin.style.display = 'inline-flex';
      if (navUserMenu) navUserMenu.style.display = 'none';
    }
  });
  
  // Nav sign in button opens auth modal
  navSignin?.addEventListener('click', () => {
    showAuthModal();
  });

  let pendingUpload = false;

  // URL input change
  pdfUrlInput?.addEventListener('input', () => {
    updateAnalyzeButton();
  });

  // File input change
  pdfFileInput?.addEventListener('change', (e) => {
    const file = e.target.files?.[0];
    if (file) {
      selectFile(file);
    }
  });

  // Drag and drop
  fileDropZone?.addEventListener('dragover', (e) => {
    e.preventDefault();
    fileDropZone.classList.add('dragover');
  });

  fileDropZone?.addEventListener('dragleave', () => {
    fileDropZone.classList.remove('dragover');
  });

  fileDropZone?.addEventListener('drop', (e) => {
    e.preventDefault();
    fileDropZone.classList.remove('dragover');
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type === 'application/pdf') {
      selectFile(file);
    } else {
      showStatus('Please drop a PDF file.', 'error');
    }
  });

  // Remove file
  fileRemove?.addEventListener('click', (e) => {
    e.stopPropagation();
    clearFile();
  });

  // Analyze button
  btnAnalyze?.addEventListener('click', async () => {
    if (!currentUser) {
      // Show auth modal
      pendingUpload = true;
      showAuthModal();
    } else {
      await processUpload();
    }
  });

  // Auth close
  authClose?.addEventListener('click', () => {
    hideAuthModal();
    pendingUpload = false;
  });

  // Click outside auth modal to close
  authOverlay?.addEventListener('click', (e) => {
    if (e.target === authOverlay) {
      hideAuthModal();
      pendingUpload = false;
    }
  });

  // Sign in
  btnSignin?.addEventListener('click', async () => {
    const email = authEmail?.value?.trim();
    const password = authPassword?.value;
    
    if (!email || !password) {
      showAuthError('Please enter email and password.');
      return;
    }

    showAuthLoading(true);
    clearAuthMessages();

    try {
      await signInWithEmailAndPassword(auth, email, password);
      showAuthSuccess('Signed in successfully!');
      setTimeout(() => hideAuthModal(), 500);
    } catch (error) {
      console.error('[Landing] Sign in error:', error);
      showAuthError(getAuthErrorMessage(error));
    } finally {
      showAuthLoading(false);
    }
  });

  // Create account
  btnCreateAccount?.addEventListener('click', async () => {
    const email = authEmail?.value?.trim();
    const password = authPassword?.value;
    
    if (!email || !password) {
      showAuthError('Please enter email and password.');
      return;
    }

    if (password.length < 6) {
      showAuthError('Password must be at least 6 characters.');
      return;
    }

    showAuthLoading(true);
    clearAuthMessages();

    try {
      await createUserWithEmailAndPassword(auth, email, password);
      showAuthSuccess('Account created successfully!');
      setTimeout(() => hideAuthModal(), 500);
    } catch (error) {
      console.error('[Landing] Create account error:', error);
      showAuthError(getAuthErrorMessage(error));
    } finally {
      showAuthLoading(false);
    }
  });

  // Google sign in
  btnGoogleSignin?.addEventListener('click', async () => {
    showAuthLoading(true);
    clearAuthMessages();

    try {
      await signInWithPopup(auth, googleProvider);
      showAuthSuccess('Signed in with Google!');
      setTimeout(() => hideAuthModal(), 500);
    } catch (error) {
      console.error('[Landing] Google sign in error:', error);
      showAuthError(getAuthErrorMessage(error));
    } finally {
      showAuthLoading(false);
    }
  });

  // Helper functions
  function selectFile(file) {
    selectedFile = file;
    if (fileSelected && selectedFileName) {
      fileSelected.style.display = 'flex';
      selectedFileName.textContent = file.name;
    }
    const dropContent = fileDropZone?.querySelector('.drop-zone-content');
    if (dropContent) dropContent.style.display = 'none';
    
    // Clear URL when file is selected
    if (pdfUrlInput) pdfUrlInput.value = '';
    updateAnalyzeButton();
  }

  function clearFile() {
    selectedFile = null;
    if (pdfFileInput) pdfFileInput.value = '';
    if (fileSelected) fileSelected.style.display = 'none';
    const dropContent = fileDropZone?.querySelector('.drop-zone-content');
    if (dropContent) dropContent.style.display = 'block';
    updateAnalyzeButton();
  }

  function updateAnalyzeButton() {
    const hasUrl = pdfUrlInput?.value?.trim()?.length > 0;
    const hasFile = selectedFile !== null;
    if (btnAnalyze) {
      btnAnalyze.disabled = !(hasUrl || hasFile);
    }
  }

  function showStatus(message, type = 'info') {
    if (uploadStatus) {
      uploadStatus.textContent = message;
      uploadStatus.className = `upload-status ${type}`;
      uploadStatus.style.display = 'block';
    }
  }

  function hideStatus() {
    if (uploadStatus) uploadStatus.style.display = 'none';
  }

  function showAuthModal() {
    if (authOverlay) authOverlay.style.display = 'flex';
    clearAuthMessages();
  }

  function hideAuthModal() {
    if (authOverlay) authOverlay.style.display = 'none';
    clearAuthMessages();
  }

  function showAuthError(message) {
    if (authError) {
      authError.textContent = message;
      authError.style.display = 'block';
    }
  }

  function showAuthSuccess(message) {
    if (authSuccess) {
      authSuccess.textContent = message;
      authSuccess.style.display = 'block';
    }
  }

  function showAuthLoading(show) {
    if (authLoading) authLoading.style.display = show ? 'flex' : 'none';
    if (btnSignin) btnSignin.disabled = show;
    if (btnCreateAccount) btnCreateAccount.disabled = show;
    if (btnGoogleSignin) btnGoogleSignin.disabled = show;
  }

  function clearAuthMessages() {
    if (authError) authError.style.display = 'none';
    if (authSuccess) authSuccess.style.display = 'none';
  }

  function getAuthErrorMessage(error) {
    switch (error.code) {
      case 'auth/invalid-email':
        return 'Invalid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/email-already-in-use':
        return 'An account already exists with this email.';
      case 'auth/weak-password':
        return 'Password is too weak.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in popup was closed.';
      default:
        return error.message || 'An error occurred.';
    }
  }

  async function processUpload() {
    pendingUpload = false;
    hideStatus();
    
    const pdfUrl = pdfUrlInput?.value?.trim();
    
    // Show loading state
    if (btnAnalyze) {
      const btnText = btnAnalyze.querySelector('.btn-text');
      const btnSpinner = btnAnalyze.querySelector('.btn-spinner');
      if (btnText) btnText.textContent = 'Processing...';
      if (btnSpinner) btnSpinner.style.display = 'inline-block';
      btnAnalyze.disabled = true;
    }

    try {
      if (selectedFile) {
        // Upload file to Firebase Storage
        showStatus('Uploading file...', 'info');
        
        const userId = currentUser.uid;
        const timestamp = Date.now();
        const filename = `${timestamp}_${selectedFile.name}`;
        const storageRef = ref(storage, `uploads/${userId}/${filename}`);
        
        await uploadBytes(storageRef, selectedFile);
        const downloadUrl = await getDownloadURL(storageRef);
        
        // Redirect to web-app with the file URL
        const encodedUrl = encodeURIComponent(downloadUrl);
        const encodedName = encodeURIComponent(selectedFile.name);
        window.location.href = `/web-app.html?source=upload&url=${encodedUrl}&name=${encodedName}`;
        
      } else if (pdfUrl) {
        // Validate URL
        try {
          new URL(pdfUrl);
        } catch {
          showStatus('Please enter a valid URL.', 'error');
          resetButton();
          return;
        }
        
        // Check if it's a Firebase Storage URL (will work) or external (may have CORS issues)
        const isFirebaseUrl = pdfUrl.includes('firebasestorage.googleapis.com') || 
                              pdfUrl.includes('storage.googleapis.com');
        
        if (!isFirebaseUrl) {
          showStatus('Note: External URLs may not load due to CORS. If it fails, please download the PDF and upload it directly.', 'info');
        }
        
        // Redirect to web-app with the URL
        const encodedUrl = encodeURIComponent(pdfUrl);
        window.location.href = `/web-app.html?source=url&url=${encodedUrl}`;
      }
    } catch (error) {
      console.error('[Landing] Upload error:', error);
      showStatus(`Error: ${error.message}`, 'error');
      resetButton();
    }
  }

  function resetButton() {
    if (btnAnalyze) {
      const btnText = btnAnalyze.querySelector('.btn-text');
      const btnSpinner = btnAnalyze.querySelector('.btn-spinner');
      if (btnText) btnText.textContent = 'Analyze Citations';
      if (btnSpinner) btnSpinner.style.display = 'none';
      btnAnalyze.disabled = false;
    }
    updateAnalyzeButton();
  }

  // Smooth scroll for anchor links
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function(e) {
      e.preventDefault();
      const target = document.querySelector(this.getAttribute('href'));
      if (target) {
        const navHeight = document.querySelector('.nav')?.offsetHeight || 0;
        const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - navHeight;
        window.scrollTo({ top: targetPosition, behavior: 'smooth' });
      }
    });
  });

  // Populate download links with version from version.json
  async function populateDownloadLinks() {
    try {
      const response = await fetch('/version.json');
      if (!response.ok) throw new Error('Failed to fetch version');
      const data = await response.json();
      const versionFromUrl = (url) => {
        const match = String(url || '').match(/pelaikan-setup-v(\d+\.\d+\.\d+)\.(exe|dmg)(?:$|[?#])/);
        return match ? match[1] : null;
      };
      const winVersion = data.winVersion || versionFromUrl(data.winUrl) || data.desktopVersion;
      const macVersion = data.macVersion || versionFromUrl(data.macUrl) || data.desktopVersion;
      
      const downloadWindows = byId('download-windows');
      const downloadMacos = byId('download-macos');
      const versionDisplay = byId('version-display');
      
      if (downloadWindows && winVersion) {
        downloadWindows.href = data.winUrl || `/downloads/pelaikan-setup-v${winVersion}.exe`;
      }
      if (downloadWindows && !winVersion) {
        downloadWindows.style.display = 'none';
      }
      if (downloadMacos && macVersion) {
        downloadMacos.href = data.macUrl || `/downloads/pelaikan-setup-v${macVersion}.dmg`;
      }
      if (downloadMacos && !macVersion) {
        downloadMacos.style.display = 'none';
      }
      if (versionDisplay) {
        versionDisplay.textContent = !winVersion && !macVersion
          ? ''
          : winVersion === macVersion
          ? `v${winVersion}`
          : [winVersion && `Windows v${winVersion}`, macVersion && `macOS v${macVersion}`].filter(Boolean).join(' · ');
      }
      
      console.log('[Landing] Download links populated:', { winVersion, macVersion, webVersion: data.latest });
    } catch (error) {
      console.error('[Landing] Error fetching version:', error);
      const versionDisplay = byId('version-display');
      if (versionDisplay) versionDisplay.textContent = '';
    }
  }
  
  populateDownloadLinks();
});
